import type { Landmarks } from '@store/handStore';
import { FINGER_TIPS } from '@engine/hand/landmarks';

export interface BendTrackerSettings {
  deadzone: number;
  fullRange: number;
  maxCents: number;
}

/**
 * Vertical bend: user holds index/middle/ring aligned on one line,
 * then presses down (Y increases) to bend DOWN or pulls up
 * (Y decreases) to bend UP.
 *
 *   Y below anchor (press down) → negative cents (lower pitch)
 *   Y above anchor (pull up)    → positive cents (higher pitch)
 *
 * Anchor + reading use the average Y of the index/middle/ring tips
 * so the 3-finger posture is robust against small finger wobble.
 */
/** Time constant (seconds) for the bend output low-pass. MediaPipe tip
 *  positions jitter by ≈ 0.005-0.01 y-units frame-to-frame even at rest;
 *  that jitter maps directly to 5-15 cents of pitch flicker at typical
 *  maxCents settings. 50 ms EMA suppresses per-frame flicker while still
 *  tracking intentional bend motion (a 100 ms press reaches ~85% of its
 *  target value — indistinguishable from the audio glide latency). */
const BEND_EMA_TAU_S = 0.05;

export class BendTracker {
  private anchorY: number | null = null;
  /** Smoothed output of `read()`. Reset on `start`/`end` so a new bend
   *  doesn't inherit the previous one's steady-state value. */
  private smoothedCents = 0;
  private lastReadAt = 0;

  constructor(private settings: BendTrackerSettings) {}

  updateSettings(s: Partial<BendTrackerSettings>): void {
    this.settings = { ...this.settings, ...s };
  }

  start(lm: Landmarks): number {
    const y = this.readBendAxis(lm);
    if (!Number.isFinite(y)) return 0;
    this.anchorY = y;
    this.smoothedCents = 0;
    this.lastReadAt = 0;
    return y;
  }

  end(): void {
    this.anchorY = null;
    this.smoothedCents = 0;
    this.lastReadAt = 0;
  }

  read(lm: Landmarks): number {
    if (this.anchorY === null) return 0;
    const y = this.readBendAxis(lm);
    if (!Number.isFinite(y)) return 0;
    const delta = y - this.anchorY; // + means pressed down
    const abs = Math.abs(delta);
    let raw = 0;
    if (abs >= this.settings.deadzone) {
      const range = Math.max(1e-4, this.settings.fullRange - this.settings.deadzone);
      const shaped = Math.sign(delta) * ((abs - this.settings.deadzone) / range);
      const clamped = Math.max(-1, Math.min(1, shaped));
      raw = -clamped * this.settings.maxCents;
    }
    if (!Number.isFinite(raw)) raw = 0;
    // Low-pass to suppress per-frame tip jitter from MediaPipe. First
    // call seeds the filter directly so the bend doesn't have to ramp
    // up from 0 on entry.
    const now = performance.now();
    if (this.lastReadAt === 0) {
      this.smoothedCents = raw;
      this.lastReadAt = now;
      return raw;
    }
    const dt = Math.max(0.001, (now - this.lastReadAt) / 1000);
    this.lastReadAt = now;
    const alpha = 1 - Math.exp(-dt / BEND_EMA_TAU_S);
    this.smoothedCents += alpha * (raw - this.smoothedCents);
    return this.smoothedCents;
  }

  isAnchored(): boolean {
    return this.anchorY !== null;
  }

  anchor(): number | null {
    return this.anchorY;
  }

  // ---------- internals ----------

  private readBendAxis(lm: Landmarks): number {
    const idx = lm[FINGER_TIPS.index]?.y;
    const mid = lm[FINGER_TIPS.middle]?.y;
    const ring = lm[FINGER_TIPS.ring]?.y;
    if (idx === undefined || mid === undefined || ring === undefined) return NaN;
    return (idx + mid + ring) / 3;
  }
}
